import html2canvas from 'html2canvas';
import {jsPDF} from 'jspdf';
import React from 'react';
import {createRoot} from 'react-dom/client'; // Updated for React 18

/**
 * Function to generate and download PDF with proper layout
 * @param {Array} pages - Array of page components to include in the PDF
 * @param {Object} studentProfile - Student information
 * @param {Function} setIsPdfGenerating - State setter for loading indicator (optional)
 */


export const generatePDF = async (pages, studentProfile, setIsPdfGenerating = null) => {
    try {
        // 1. Show loading indicator
        if (setIsPdfGenerating) {
            setIsPdfGenerating(true);
        }

        // 2. Define A4 dimensions in points (72 points per inch)
        // A4 is 210×297mm or 8.27×11.69 inches
        const PDF_WIDTH = 210;
        const PDF_HEIGHT = 297;

        // 3. Create a new PDF with A4 dimensions
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true
        });

        // 4. Create a temporary container for rendering pages
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.top = '0';
        container.style.width = `${PDF_WIDTH}mm`;
        container.style.height = `${PDF_HEIGHT}mm`;
        container.style.backgroundColor = 'white';
        container.style.zIndex = '-1000';
        document.body.appendChild(container);

        // Make sure all web fonts are loaded before proceeding
        // This addresses the Typekit 404 error
        await document.fonts.ready;

        // Preload any custom fonts needed
        const fontStyle = document.createElement('style');
        fontStyle.textContent = `
      @font-face {
        font-family: 'Poppins';
        src: local('Arial'), local('Helvetica'), local('sans-serif');
        font-weight: normal;
        font-style: normal;
      }
      body {
        font-family: 'Poppins', sans-serif !important;
      }
    `;
        document.head.appendChild(fontStyle);

        // 5. Process each page
        for (let i = 0; i < pages.length; i++) {
            const page = pages[i];

            // Create page wrapper with proper A4 dimensions
            const pageDiv = document.createElement('div');
            pageDiv.style.width = `${PDF_WIDTH}mm`;
            pageDiv.style.height = `${PDF_HEIGHT}mm`;
            pageDiv.style.backgroundColor = 'white';
            pageDiv.style.overflow = 'hidden';
            pageDiv.style.position = 'relative';
            pageDiv.style.boxSizing = 'border-box';
            pageDiv.style.display = 'block';
            pageDiv.className = 'pdf-page-container';

            // Add content container with proper margins
            const contentDiv = document.createElement('div');
            contentDiv.style.position = 'absolute';
            contentDiv.style.top = '0';
            contentDiv.style.left = '0';
            contentDiv.style.right = '0';
            contentDiv.style.bottom = '0';
            contentDiv.style.width = '100%';
            contentDiv.style.height = '100%';
            contentDiv.style.display = 'block';
            contentDiv.style.backgroundColor = 'white';

            // Render React component using createRoot (React 18)
            const tempDiv = document.createElement('div');
            tempDiv.style.width = '100%';
            tempDiv.style.height = '100%';
            tempDiv.style.display = 'block';
            tempDiv.style.backgroundColor = 'white';

            // Using React 18's createRoot
            const root = createRoot(tempDiv);
            root.render(page.component);

            // Wait for rendering to complete - this is crucial
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Append component to content div
            contentDiv.appendChild(tempDiv);

            // Add content to page
            pageDiv.appendChild(contentDiv);

            // Clear container and append new page
            container.innerHTML = '';
            container.appendChild(pageDiv);

            // Wait to ensure everything is rendered properly
            await new Promise(resolve => setTimeout(resolve, 1000));

            try {
                // Fix for SVGs and other elements with 0 dimensions
                const fixZeroDimensions = (element) => {
                    // Check for SVGs and canvas elements
                    const svgs = element.querySelectorAll('svg');
                    const canvases = element.querySelectorAll('canvas');
                    const images = element.querySelectorAll('img');

                    // Ensure all SVGs have dimensions
                    svgs.forEach(svg => {
                        if (!svg.getAttribute('width') || svg.getAttribute('width') === '0') {
                            svg.setAttribute('width', '20');
                        }
                        if (!svg.getAttribute('height') || svg.getAttribute('height') === '0') {
                            svg.setAttribute('height', '20');
                        }
                        svg.style.display = 'inline-block';
                    });

                    // Ensure all canvases have dimensions
                    canvases.forEach(canvas => {
                        if (canvas.width === 0) canvas.width = 20;
                        if (canvas.height === 0) canvas.height = 20;
                    });

                    // Ensure all images have dimensions
                    images.forEach(img => {
                        if (img.width === 0) img.width = 20;
                        if (img.height === 0) img.height = 20;
                        // Make sure img src is valid
                        if (!img.complete) {
                            img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
                        }
                    });
                };

                // Apply dimension fixes
                fixZeroDimensions(pageDiv);

                // Use html2canvas with more reliable settings
                const canvas = await html2canvas(pageDiv, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                    ignoreElements: el =>
                        el.classList && el.classList.contains("no-pdf-render"),
                    backgroundColor: '#ffffff',
                    imageTimeout: 0, // Set to 0 to disable timeout
                    onclone: (clonedDoc) => {
                        // Fix dimensions in the cloned document too
                        fixZeroDimensions(clonedDoc.body);

                        // Apply additional styles to cloned document
                        const style = clonedDoc.createElement('style');
                        style.innerHTML = `
              * { 
                -webkit-print-color-adjust: exact !important; 
                print-color-adjust: exact !important;
                font-family: 'Poppins', Arial, sans-serif !important;
              }
              .bg-sky-200 { background-color: #bae6fd !important; }
              .bg-amber-50 { background-color: #fffbeb !important; }
              .bg-amber-100 { background-color: #fef3c7 !important; }
              .bg-amber-200 { background-color: #fde68a !important; }
              .bg-red-200 { background-color: #fecaca !important; }
              .bg-blue-800 { background-color: #1e40af !important; }
              svg { 
                display: inline-block !important; 
                width: 20px !important; 
                height: 20px !important;
              }
              canvas { 
                display: inline-block !important; 
                min-width: 20px !important; 
                min-height: 20px !important;
              }
              img {
                display: inline-block !important;
                min-width: 20px !important;
                min-height: 20px !important;
              }
            `;
                        clonedDoc.head.appendChild(style);
                    }
                });

                // Convert canvas to image and add to PDF
                const imgData = canvas.toDataURL('image/jpeg', 1.0);

                // Add page to PDF (first page doesn't need addPage call)
                if (i > 0) {
                    pdf.addPage();
                }

                // Add image to cover the entire page
                pdf.addImage(imgData, 'JPEG', 0, 0, PDF_WIDTH, PDF_HEIGHT, '', 'FAST');

                // Update progress
                console.log(`Processed page ${i + 1} of ${pages.length}`);

            } catch (pageError) {
                console.error(`Error processing page ${i + 1}:`, pageError);
                // Continue to next page rather than failing completely
                if (i > 0) {
                    pdf.addPage();
                }
                // Add error message on page
                pdf.setFontSize(20);
                pdf.setTextColor(255, 0, 0);
                pdf.text(`Error generating page ${i + 1}`, 20, 20);
                pdf.setFontSize(12);
                pdf.text(`${pageError.message}`, 20, 30);
            }
        }

        // 6. Save the PDF
        const safeFilename = studentProfile?.name
            ? studentProfile.name.replace(/[^a-z0-9]/gi, '_')
            : 'Student';
        const filename = `${safeFilename}_Progress_Report.pdf`;
        pdf.save(filename);

        // 7. Clean up
        document.body.removeChild(container);
        document.head.removeChild(fontStyle);

    } catch (error) {
        console.error('Error generating PDF:', error);
        alert('There was an error generating the PDF. Please try again.');
    } finally {
        if (setIsPdfGenerating) {
            setIsPdfGenerating(false);
        }
    }
};

/**
 * Alternative direct printing approach that uses the browser's print functionality
 * @param {Array} pages - Array of page components to include in the print
 */
export const printReport = async (pages) => {
    // 1. Create print-specific stylesheet if not exists
    if (!document.getElementById('print-stylesheet')) {
        const style = document.createElement('style');
        style.id = 'print-stylesheet';
        style.textContent = `
      @media print {
        @page {
          size: A4;
          margin: 0;
        }
        
        body {
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        .print-page {
          page-break-after: always;
          width: 210mm;
          height: 297mm;
          position: relative;
          margin: 0 auto;
          padding: 0;
          box-sizing: border-box;
          overflow: visible;
          display: block;
        }
        
        .print-page > * {
          width: 100%;
          height: 100%;
          box-sizing: border-box;
        }
        
        .print-hidden {
          display: none !important;
        }
        
        /* Fix for table layouts */
        table { width: 100% !important; }
        
        /* Fix background colors */
        .bg-sky-200 { background-color: #bae6fd !important; }
        .bg-amber-50 { background-color: #fffbeb !important; }
        .bg-amber-100 { background-color: #fef3c7 !important; }
        .bg-amber-200 { background-color: #fde68a !important; }
        .bg-red-200 { background-color: #fecaca !important; }
        .bg-blue-800 { background-color: #1e40af !important; }
        
        /* Ensure SVG icons appear */
        svg { 
          display: inline-block !important;
          min-width: 20px !important;
          min-height: 20px !important;
        }
      }
    `;
        document.head.appendChild(style);
    }

    // 2. Create or update the print container
    let printContainer = document.getElementById('print-container');
    if (!printContainer) {
        printContainer = document.createElement('div');
        printContainer.id = 'print-container';
        printContainer.className = 'hidden print-only';
        document.body.appendChild(printContainer);
    }

    // 3. Clear and populate print container
    printContainer.innerHTML = '';

    // Make sure all web fonts are loaded before proceeding
    await document.fonts.ready;

    // 4. Add each page with proper A4 sizing
    for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const pageDiv = document.createElement('div');
        pageDiv.className = 'print-page';

        // Clone the current page content
        const contentDiv = document.createElement('div');
        contentDiv.className = 'print-content';

        // Use React 18's createRoot
        const root = createRoot(contentDiv);
        root.render(page.component);

        pageDiv.appendChild(contentDiv);
        printContainer.appendChild(pageDiv);
    }

    // 5. Ensure all content is loaded before printing
    setTimeout(() => {
        window.print();
    }, 1000);
};

/**
 * Combined handler that decides between PDF download and print
 * @param {boolean} downloadAsPdf - Whether to download as PDF or print
 * @param {Array} pages - Array of page components
 * @param {Object} studentProfile - Student information
 * @param {Function} setIsPdfGenerating - State setter for loading indicator (optional)
 */
export const handlePrint = (downloadAsPdf, pages, studentProfile, setIsPdfGenerating = null) => {
    if (downloadAsPdf) {
        generatePDF(pages, studentProfile, setIsPdfGenerating);
    } else {
        printReport(pages);
    }
};

/**
 * Improved component for showing generated pages with better A4 preview
 */
export const ReportPreview = ({currentPage, pages}) => {
    return (
        <div className="bg-gray-100 font-[poppins] rounded-lg shadow p-4 relative">
            <div
                className="bg-white shadow-lg mx-auto relative overflow-hidden"
                style={{
                    width: '210mm',
                    height: '297mm',
                    maxHeight: '80vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start'
                }}
            >
                <div
                    className="w-full h-full"
                    style={{
                        transform: 'scale(0.98)',
                        transformOrigin: 'top center',
                        overflow: 'auto'
                    }}
                    data-page-index={currentPage}
                >
                    {pages[currentPage]?.component}
                </div>

                {/* Page number indicator */}
                <div className="absolute bottom-2 right-4 bg-gray-200 px-3 py-1 rounded-full text-sm">
                    {currentPage + 1} / {pages.length}
                </div>
            </div>
        </div>
    );
};